import React, { useMemo } from 'react';
import { getOutlierStyle } from '../top_ranker/statMetrics';
import { MY_TEAM_INDEX } from './liveAssistState';
import { buildCardView } from './cardStats';
import PowerCurveSvg from './PowerCurveSvg';
import { buildCurveSeries } from './curveSeries';
import usePowerCurves, { powerCurveKey } from './usePowerCurves';

// PiP 안의 조합 표시. 인라인 스타일만 쓴다(별도 창이라 CSS가 없음).
// - 요약: 전체 팀을 한 줄씩, 카드 3칸에 캐릭터/무기와 핵심 지표
// - 상세: 선택한 팀의 파워 커브 그래프 1개(팀원 3명의 선을 색으로 구분) + 색 띠를 단 카드 지표

export const SERIES_COLORS = ['#378ADD', '#D85A30', '#1D9E75'];
const MUTED = '#9aa0a6';
const WARN = '#faad14';
const MINE = '#52c41a'; // 우리 팀 표시색

const fmt1 = (v) => (v === null || v === undefined ? '-' : v.toFixed(1));
const fmtSigned = (v) => (v === null || v === undefined ? '-' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}`);
const fmtDamage = (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(Math.round(v)));

function Portrait({ name, weapon }) {
  return (
    <div style={{ position: 'relative', width: 34, height: 34, flexShrink: 0 }}>
      <div style={{ width: 34, height: 34, borderRadius: '50%', background: '#3a3a4d', overflow: 'hidden' }}>
        <img
          src={`/img/character/${name}.png`}
          alt={name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      </div>
      {weapon && (
        <img
          src={`/img/weapon_skill/${weapon}.png`}
          alt={weapon}
          style={{
            position: 'absolute', right: -5, bottom: -2, width: 15, height: 15, borderRadius: '50%',
            background: '#323232', padding: 2, border: '1px solid #4a4a4a', boxSizing: 'border-box',
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
    </div>
  );
}

function Metric({ label, value, style }) {
  return (
    <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
      <span style={{ color: MUTED }}>{label} </span>
      <span style={style}>{value}</span>
    </span>
  );
}

function CardMetrics({ view, full }) {
  const { metrics: m, averages: a } = view;
  if (!m) {
    return <div style={{ color: MUTED }}>통계 없음</div>;
  }
  if (!m.hasGames) {
    return <div style={{ color: MUTED }}>표본 없음</div>;
  }
  return (
    <div style={{ lineHeight: '17px' }}>
      <Metric label="픽" value={`${m.pickRate.toFixed(1)}%`} />
      <Metric label="승" value={`${m.winRate.toFixed(1)}%`} style={getOutlierStyle(m.winRate / 100, a.avgWinRate, false)} />
      {full && <Metric label="Top3" value={`${m.top3Rate.toFixed(1)}%`} />}
      <br />
      <Metric label="RP" value={fmtSigned(m.rpGain)} style={m.rpGain === null ? undefined : getOutlierStyle(m.rpGain, a.avgMmrGain, false, 0.2)} />
      {full && (
        <Metric
          label="평균"
          value={`${fmt1(m.avgRank)}등`}
          style={m.avgRank === null ? undefined : getOutlierStyle(m.avgRank, a.avgRank, true, 0.05)}
        />
      )}
      <Metric label="딜" value={fmtDamage(m.dmgPerGame)} />
      {full && (
        <>
          <br />
          <Metric label="킬" value={m.killsPerGame.toFixed(1)} />
          <Metric label="TK" value={m.tkPerGame.toFixed(1)} />
          <Metric label="dps" value={m.dps.toFixed(1)} />
        </>
      )}
    </div>
  );
}

function CardTitle({ view }) {
  return (
    <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {view.characterName}
      {view.weaponName && <span style={{ fontWeight: 400, color: MUTED }}> {view.weaponName}</span>}
      {view.weaponEstimated && <span style={{ marginLeft: 4, fontSize: 11, color: WARN }}>무기 추정</span>}
      {view.lowConfidence && <span style={{ marginLeft: 4, fontSize: 11, color: WARN }}>확인 필요</span>}
    </div>
  );
}

function SummaryRow({ index, views, onSelect, mine }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(index)}
      style={{
        display: 'grid', gridTemplateColumns: '24px repeat(3, minmax(0, 1fr))', gap: 8, alignItems: 'center',
        width: '100%', padding: '6px 8px', marginBottom: 5, textAlign: 'left', cursor: 'pointer',
        background: '#2a2a3d', border: mine ? `1px solid ${MINE}` : '1px solid #3a3a4d', borderRadius: 6, color: 'inherit', font: 'inherit',
      }}
    >
      <span style={{ textAlign: 'center', color: mine ? MINE : MUTED, fontSize: 12, lineHeight: '14px' }}>
        {index + 1}
        {mine && <><br />우리</>}
      </span>
      {views.map((view, i) => (
        <div key={`${index}-${i}`} style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
          <Portrait name={view.characterName} weapon={view.weaponName} />
          <div style={{ minWidth: 0, fontSize: 12 }}>
            <CardTitle view={view} />
            <CardMetrics view={view} />
          </div>
        </div>
      ))}
    </button>
  );
}

function TeamDetail({ index, views, onBack, mine, referenceViews, currentPhase }) {
  // 우리 팀이 아닌 팀을 볼 때는 우리 팀의 파워 커브도 연하게 같이 그린다(우리 팀을 볼 때는 겹쳐 그릴 필요가 없다).
  const entries = [...views, ...(referenceViews || [])].map((v) => ({ characterName: v.characterName, weaponName: v.weaponName }));
  const curves = usePowerCurves(entries);
  const curveOf = (v) => curves[powerCurveKey(v.characterName, v.weaponName)] || { status: 'loading', points: [] };
  const series = buildCurveSeries({ views, referenceViews, curveOf, colors: SERIES_COLORS });
  const loading = [...views, ...(referenceViews || [])].some((v) => curveOf(v).status === 'loading');

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        style={{
          display: 'block', width: '100%', marginBottom: 8, padding: '6px 10px', textAlign: 'left', cursor: 'pointer',
          background: '#2a2a3d', border: '1px solid #3a3a4d', borderRadius: 6, color: 'inherit', font: 'inherit', fontSize: 13,
        }}
      >
        ← {index + 1}팀 상세 <span style={{ color: MUTED }}>(누르면 전체 요약으로)</span>
        {mine && <span style={{ marginLeft: 8, color: MINE }}>우리 팀</span>}
      </button>
      <div style={{ background: '#fff', borderRadius: 6, padding: '8px 4px 4px', marginBottom: 8, minHeight: 120 }}>
        {loading && !series.some((s) => s.points.length) ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#8c8c8c', fontSize: 13 }}>파워 커브 불러오는 중…</div>
        ) : (
          <PowerCurveSvg series={series} current={currentPhase} />
        )}
      </div>
      {referenceViews && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 12, color: MUTED }}>
          <span>연한 점선: 우리 팀</span>
          {referenceViews.map((v, i) => (
            <span key={`ref-${v.characterName}-${i}`} style={{ whiteSpace: 'nowrap' }}>
              <span style={{ display: 'inline-block', width: 14, borderTop: `2px dashed ${SERIES_COLORS[i % SERIES_COLORS.length]}`, opacity: 0.6, verticalAlign: 'middle', marginRight: 4 }} />
              {v.characterName}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
        {views.map((view, i) => (
          <div
            key={`${view.characterName}-${i}`}
            style={{
              background: '#2a2a3d', borderRadius: 6, padding: '8px 10px', fontSize: 12, minWidth: 0,
              borderTop: `3px solid ${SERIES_COLORS[i % SERIES_COLORS.length]}`,
            }}
          >
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
              <Portrait name={view.characterName} weapon={view.weaponName} />
              <CardTitle view={view} />
            </div>
            <CardMetrics view={view} full />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PipTeamsView({ teams, selectedTeam, onSelectTeam, topRanker, currentPhase = null }) {
  const viewsByTeam = useMemo(
    () => teams.map((team) => team.map((card) => buildCardView(card, topRanker.stats, topRanker.totalGameCount, topRanker.averages))),
    [teams, topRanker.stats, topRanker.totalGameCount, topRanker.averages]
  );

  if (topRanker.loading) {
    return <div style={{ padding: 16, color: MUTED }}>통계 불러오는 중…</div>;
  }
  if (topRanker.error) {
    return <div style={{ padding: 16, color: '#ff7875' }}>{topRanker.error}</div>;
  }
  if (selectedTeam !== null && viewsByTeam[selectedTeam]) {
    const mine = selectedTeam === MY_TEAM_INDEX;
    return (
      <TeamDetail
        index={selectedTeam}
        views={viewsByTeam[selectedTeam]}
        onBack={() => onSelectTeam(selectedTeam)}
        mine={mine}
        referenceViews={mine ? null : viewsByTeam[MY_TEAM_INDEX] || null}
        currentPhase={currentPhase}
      />
    );
  }
  return (
    <div>
      {viewsByTeam.map((views, index) => (
        <SummaryRow key={`team-${index}`} index={index} views={views} onSelect={onSelectTeam} mine={index === MY_TEAM_INDEX} />
      ))}
    </div>
  );
}
